import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useLang } from "../i18n/LangContext";
import { Card, CardContent } from "./ui/card";
import { StatusBadge, formatIDR } from "./Shared";
import { DASH } from "../constants/testIds";
import { Wallet, Clock, Users, GraduationCap, ArrowRight, CalendarClock } from "lucide-react";

export function AdminSummary() {
  const { t } = useLang();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/dashboard/summary").then(({ data }) => setS(data.data)).catch(() => {}); }, []);
  if (!s) return null;

  const kpis = [
    { icon: Wallet, label: t("admin.revenue"), value: formatIDR(s.revenue_total), sub: `${s.approved_payments} ${t("admin.approvedPayments")}`, testId: DASH.revenue, accent: "text-[#F59E0B]" },
    { icon: Clock, label: t("admin.pendingPayments"), value: s.pending_payments, sub: formatIDR(s.pending_amount), testId: DASH.pending, accent: "text-amber-300", to: "/admin/payments" },
    { icon: Users, label: t("admin.members"), value: s.member_accounts, sub: `${s.active_participants} ${t("admin.activeParticipants")} · ${s.coach_accounts} Coach`, testId: DASH.members, accent: "text-emerald-300" },
    { icon: GraduationCap, label: t("admin.pendingCandidates"), value: s.candidates_waiting_review, sub: `${s.candidates_in_qualification} ${t("admin.inQualification")}`, testId: DASH.candidates, accent: "text-sky-300", to: "/admin/coach-candidates" },
  ];

  const tracking = [
    { icon: Users, label: t("admin.activeMembers"), value: s.active_members ?? 0, sub: `${s.members_active_enrollment ?? 0} ${t("admin.withEnrollment")} · ${s.members_expired_enrollment ?? 0} ${t("admin.expiredShort")}`, testId: "dash-active-members", accent: "text-emerald-300" },
    { icon: GraduationCap, label: t("admin.activeCoaches"), value: s.active_coaches ?? 0, sub: `${s.coaches_active_enrollment ?? 0} ${t("admin.withEnrollment")} · ${s.coaches_expired_enrollment ?? 0} ${t("admin.expiredShort")}`, testId: "dash-active-coaches", accent: "text-sky-300" },
    { icon: CalendarClock, label: t("admin.activeEnrollment"), value: s.active_enrollments ?? 0, sub: `${s.expiring_7_days ?? 0} ${t("admin.expiring7")} · ${s.expiring_14_days ?? 0} ${t("admin.expiring14")}`, testId: "dash-active-enrollment", accent: "text-[#F59E0B]" },
    { icon: CalendarClock, label: t("admin.expiredEnrollment"), value: s.expired_enrollments ?? 0, sub: t("admin.expiredEnrollmentSub"), testId: "dash-expired-enrollment", accent: "text-red-300" },
  ];

  return (
    <div data-testid={DASH.container} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const inner = (
            <Card className="bg-[#161923] border-white/5 hover:border-white/15 transition h-full">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-medium flex items-center gap-2"><k.icon className="h-3.5 w-3.5" /> {k.label}</div>
                <div data-testid={k.testId} className={`mt-3 font-[Outfit] text-3xl font-extrabold ${k.accent}`}>{k.value}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">{k.sub}{k.to && <ArrowRight className="h-3.5 w-3.5" />}</div>
              </CardContent>
            </Card>
          );
          return k.to ? <Link key={k.label} to={k.to}>{inner}</Link> : <div key={k.label}>{inner}</div>;
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dash-tracking-row">
        {tracking.map((k) => (
          <Card key={k.label} className="bg-[#161923] border-white/5 h-full">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-medium flex items-center gap-2"><k.icon className="h-3.5 w-3.5" /> {k.label}</div>
              <div data-testid={k.testId} className={`mt-3 font-[Outfit] text-3xl font-extrabold ${k.accent}`}>{k.value}</div>
              <div className="text-xs text-slate-500 mt-1 truncate">{k.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-[#161923] border-white/5">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-white/5 font-[Outfit] font-bold text-white">{t("admin.batchTable")}</div>
          {s.batches.length === 0 ? <p className="p-5 text-sm text-slate-500">{t("programs.noBatches")}</p> : (
            <div className="overflow-x-auto -mx-1 px-1">
              <table data-testid={DASH.batchTable} className="w-full min-w-[560px] text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500">
                  <tr className="border-b border-white/5">
                    <th className="text-left px-5 py-3 font-medium">Batch</th>
                    <th className="text-left px-3 py-3 font-medium">{t("common.status")}</th>
                    <th className="text-right px-3 py-3 font-medium">{t("admin.participants")}</th>
                    <th className="text-right px-3 py-3 font-medium">{t("payments.tabPENDING")}</th>
                    <th className="text-right px-3 py-3 font-medium">Coach</th>
                    <th className="text-right px-5 py-3 font-medium">{t("admin.revenue")}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.batches.map((b) => (
                    <tr key={b.batch_id} data-testid={`dash-batch-${b.batch_id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                      <td className="px-5 py-3"><div className="text-white font-medium">{b.name}</div><div className="text-xs text-slate-500">{b.program_name} · {b.code}</div></td>
                      <td className="px-3 py-3"><StatusBadge status={b.status} /></td>
                      <td className="px-3 py-3 text-right text-white font-semibold">{b.active}<span className="text-slate-500 font-normal">/{b.capacity}</span></td>
                      <td className="px-3 py-3 text-right text-amber-300">{b.pending}</td>
                      <td className="px-3 py-3 text-right text-slate-300">{b.coaches}</td>
                      <td className="px-5 py-3 text-right text-[#F59E0B] font-semibold">{formatIDR(b.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
